import { usePowerballWeek } from "../../powerball/usePowerballWeek.ts";
import { LegCard } from "./PowerballPage.tsx";
import { useState } from "react";
import { PowerballWorkspaceNav, POWERBALL_TIERS } from "./PowerballWorkspace.tsx";
// PB-G2 — Powerball season-history page (Gap 5 / AC-5.1–5.4).
//
// Football Products (FpKit) Kalshi-clean — inline FpKit tokens, hairline
// borders, mono uppercase labels, neutral surfaces. Accent (gold) appears
// only on typography/interactive indicators, never on cards or backgrounds.

import { useNavigate } from "react-router-dom";
import { usePowerballHistory } from "../../powerball/usePowerballHistory.ts";
import { computePowerballHistoryStats } from "../../powerball/historyStats.ts";
import { FpHead, FpAwait } from "../football/_fpkit.tsx";
import type { PbWeekSummary } from "../../powerball/usePowerballHistory.ts";

function fmt(n: number): string {
  return n.toLocaleString("en-GH", { minimumFractionDigits: 0, maximumFractionDigits: 0 });
}

function fmtOdds(n: number | null): string {
  if (n === null || !Number.isFinite(n) || n <= 0) return "—";
  if (n >= 1000) return `${Math.round(n).toLocaleString("en-GH")}×`;
  return `${n.toFixed(2)}×`;
}

// ── Inline FpKit primitives ─────────────────────────────────

function ResultBadge({ result }: { result: string | null }): JSX.Element {
  const map: Record<string, { label: string; fg: string; bg: string; bd: string }> = {
    jackpot: {
      label: "Jackpot",
      fg: "var(--fp-won)",
      bg: "var(--fp-won-soft)",
      bd: "var(--fp-won)",
    },
    near_miss: {
      label: "Near miss",
      fg: "var(--fp-accent-text)",
      bg: "var(--fp-accent-soft)",
      bd: "var(--fp-accent)",
    },
    void: {
      label: "Void",
      fg: "var(--text-secondary)",
      bg: "var(--bg-inset)",
      bd: "var(--border)",
    },
    loss: { label: "Loss", fg: "var(--fp-lost)", bg: "var(--fp-lost-soft)", bd: "var(--fp-lost)" },
  };
  const s = (result && map[result.startsWith("near_miss") ? "near_miss" : result]) || {
    label: "Pending",
    fg: "var(--text-tertiary)",
    bg: "var(--bg-inset)",
    bd: "var(--border)",
  };
  return (
    <span
      style={{
        display: "inline-flex",
        alignItems: "center",
        fontFamily: "var(--font-mono)",
        fontSize: 10,
        fontWeight: 700,
        textTransform: "uppercase",
        letterSpacing: "0.06em",
        padding: "3px 8px",
        borderRadius: 5,
        whiteSpace: "nowrap",
        color: s.fg,
        background: s.bg,
        border: `1px solid ${s.bd}`,
      }}
    >
      {s.label}
    </span>
  );
}

function SlipPill({ children }: { children: React.ReactNode }): JSX.Element {
  return (
    <span
      style={{
        display: "inline-flex",
        alignItems: "center",
        fontFamily: "var(--font-mono)",
        fontSize: 10,
        fontWeight: 600,
        textTransform: "uppercase",
        letterSpacing: "0.06em",
        padding: "3px 8px",
        borderRadius: 5,
        whiteSpace: "nowrap",
        color: "var(--text-secondary)",
        background: "var(--bg-inset)",
        border: "1px solid var(--border-faint)",
      }}
    >
      {children}
    </span>
  );
}

function StatTile({ label, value }: { label: string; value: string }): JSX.Element {
  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
      <div
        style={{
          fontFamily: "var(--font-mono)",
          fontSize: 10,
          textTransform: "uppercase",
          letterSpacing: "0.1em",
          color: "var(--text-tertiary)",
        }}
      >
        {label}
      </div>
      <div
        style={{
          fontFamily: "'Bebas Neue', sans-serif",
          fontSize: 28,
          lineHeight: 1,
          color: "var(--text)",
          letterSpacing: "0.01em",
        }}
      >
        {value}
      </div>
    </div>
  );
}

interface Row {
  sessionDate?: string | null;
  publicationId?: string | null;
  publicationVersion?: number | null;
  weekNumber: number;
  publishedAt: string | null;
  slipType: "PB10" | "MEGA25";
  combinedOdds: number | null;
  result: string | null;
}

function buildRows(weeks: readonly PbWeekSummary[]): Row[] {
  const rows: Row[] = [];
  for (const w of weeks) {
    if (w.pb10_status) {
      rows.push({
        weekNumber: w.week_number,
        sessionDate: w.session_date,
        publicationId: w.pb10_publication_id,
        publicationVersion: w.pb10_publication_version,
        publishedAt: w.published_at,
        slipType: "PB10",
        combinedOdds: w.pb10_combined_odds,
        result: w.pb10_result,
      });
    }
    if (w.mega_status) {
      rows.push({
        weekNumber: w.week_number,
        sessionDate: w.session_date,
        publicationId: w.mega_publication_id,
        publicationVersion: w.mega_publication_version,
        publishedAt: w.published_at,
        slipType: "MEGA25",
        combinedOdds: w.mega_combined_odds,
        result: w.mega_result,
      });
    }
  }
  return rows.sort(
    (a, b) =>
      (b.sessionDate ?? "").localeCompare(a.sessionDate ?? "") || b.weekNumber - a.weekNumber,
  );
}

function HistorySelectionLedger({ id, date }: { id: string; date: string }) {
  const record = usePowerballWeek(null, { sessionDate: date, publicationId: id });
  const slip = [
    record.pb10,
    record.mega20,
    record.mixedMega,
    record.lowerLines,
    record.goalsBank,
    record.bigStage,
    record.multiverse,
  ].find((s) => s?.publication_id === id);
  if (record.loading) return <p role="status">Loading original selections…</p>;
  if (record.error || !slip)
    return (
      <p role="status">
        The selection ledger could not be loaded. Open the original board to inspect the saved
        record.
      </p>
    );
  return (
    <section className="pb-history-selection-ledger" aria-label="Original selection ledger">
      <h3>Selection ledger</h3>
      <p className="pb-subtle">Original selections and prices · settlement updates separately.</p>
      {slip.selections.map((leg, index) => (
        <LegCard key={leg.id} leg={leg} index={index} />
      ))}
    </section>
  );
}

export function PowerballHistoryPage(): JSX.Element {
  const navigate = useNavigate();
  const { weeks, publications = [], loading, error } = usePowerballHistory();
  const [tierFilter, setTierFilter] = useState("all");
  const [dateFilter, setDateFilter] = useState("");
  const [showLedger, setShowLedger] = useState(false);
  const [selectedId, setSelectedId] = useState<string | null>(null);

  if (loading) {
    return (
      <div className="fp pb-workspace" data-screen-label="Powerball · Season History">
        <FpHead kicker="POWERBALL · HISTORY" title="Loading…" />
        <FpAwait
          kicker="POWERBALL · HISTORY"
          title="Loading…"
          body="Fetching season history."
          icon="clock"
          ghosts={4}
        />
      </div>
    );
  }

  if (error) {
    return (
      <div className="fp pb-workspace" data-screen-label="Powerball · Season History">
        <FpHead kicker="POWERBALL · HISTORY" title="History unavailable" />
        <FpAwait
          kicker="POWERBALL · HISTORY"
          title="History unavailable."
          body={error}
          icon="info"
          ghosts={0}
        />
      </div>
    );
  }

  const stats = computePowerballHistoryStats(weeks);
  const rows = buildRows(weeks);

  const entries = [
    ...publications
      .filter((p) => p.tier !== "perfect10")
      .map((p) => ({
        id: p.publication_id,
        title: POWERBALL_TIERS.find((t) => t.tier === p.tier)?.title ?? p.tier.replaceAll("_", " "),
        tier: p.tier,
        date: p.session_date,
        version: p.version,
        legs: p.leg_count,
        odds: p.combined_odds,
        week: 0,
        result: rows.find((r) => r.publicationId === p.publication_id)?.result ?? null,
      })),
    ...rows
      .filter(
        (r) => !r.publicationId || !publications.some((p) => p.publication_id === r.publicationId),
      )
      .map((r) => ({
        id: r.publicationId ?? `legacy-${r.weekNumber}-${r.slipType}`,
        title: r.slipType,
        tier: r.slipType === "PB10" ? "powerball_10" : "mega_powerball_20",
        date: r.sessionDate ?? "",
        version: r.publicationVersion ?? null,
        legs: null,
        odds: r.combinedOdds,
        week: r.weekNumber,
        result: r.result,
      })),
  ].sort(
    (a, b) =>
      b.date.localeCompare(a.date) || b.week - a.week || (b.version ?? 0) - (a.version ?? 0),
  );
  const filtered = entries.filter(
    (e) =>
      (tierFilter === "all" || e.tier === tierFilter) && (!dateFilter || e.date === dateFilter),
  );
  const selected = filtered.find((e) => e.id === selectedId) ?? filtered[0];
  function openOriginal(entry: (typeof entries)[number]) {
    const r = { weekNumber: entry.week };
    if (entry.id.startsWith("legacy-")) navigate(`/powerball/${r.weekNumber}`);
    else
      navigate(
        `/powerball?date=${entry.date}&publication=${entry.id}&tab=${POWERBALL_TIERS.find((t) => t.tier === entry.tier)?.tab ?? "p10"}`,
      );
  }
  return (
    <div className="fp pb-workspace" data-screen-label="Powerball · Season History">
      <PowerballWorkspaceNav active="history" />
      <header className="pb-workspace-header">
        <div>
          <span className="pb-eyebrow">THE PUBLICATION RECORD</span>
          <h1>History &amp; settlement</h1>
          <p>Every saved version, with its original selections and prices preserved.</p>
        </div>
        <button
          className="pb-secondary-button"
          aria-label="Back to Powerball"
          onClick={() => navigate("/powerball")}
        >
          ← Back to Powerball
        </button>
      </header>
      <div className="pb-history-filters">
        <label>
          Session{" "}
          <input type="date" value={dateFilter} onChange={(e) => setDateFilter(e.target.value)} />
        </label>
        <label>
          Tier{" "}
          <select value={tierFilter} onChange={(e) => setTierFilter(e.target.value)}>
            <option value="all">All tiers</option>
            {POWERBALL_TIERS.map((t) => (
              <option key={t.tier} value={t.tier}>
                {t.title}
              </option>
            ))}
          </select>
        </label>
        {(dateFilter || tierFilter !== "all") && (
          <button
            className="pb-secondary-button"
            onClick={() => {
              setDateFilter("");
              setTierFilter("all");
            }}
          >
            Clear filters
          </button>
        )}
        <span className="pb-subtle">{filtered.length} saved versions</span>
      </div>
      {filtered.length === 0 ? (
        <section className="pb-history-detail">
          <h2>{entries.length ? "No matching publications" : "No history yet."}</h2>
          <p>
            {entries.length
              ? "Choose another session or tier."
              : "Saved publications will appear here when available."}
          </p>
        </section>
      ) : (
        <div className="pb-history-layout">
          <section className="pb-history-list" aria-label="Published ticket versions">
            {filtered.map((e) => (
              <button
                key={e.id}
                type="button"
                className="pb-history-entry"
                aria-pressed={selected?.id === e.id}
                aria-label={
                  e.week
                    ? `Open week ${e.week} ${e.title}`
                    : `Inspect ${e.title} ${e.date} version ${e.version}`
                }
                onClick={() => {
                  setSelectedId(e.id);
                  setShowLedger(false);
                }}
              >
                <span>
                  <strong>{e.title}</strong>
                  <small>{e.version ? `v${e.version}` : "Legacy record"}</small>
                </span>
                <small>
                  {e.date || `Week ${e.week}`}
                  {e.legs !== null ? ` · ${e.legs} selections` : ""}
                </small>
                <span>
                  <small>Original {fmtOdds(e.odds)}</small>
                  {e.result ? (
                    <ResultBadge result={e.result} />
                  ) : (
                    <span className="pb-state-tag">View record</span>
                  )}
                </span>
              </button>
            ))}
          </section>
          {selected && (
            <section className="pb-history-detail" aria-label="Selected publication">
              <span className="pb-eyebrow">SAVED PUBLICATION</span>
              <h2 style={{ marginTop: 12 }}>
                {selected.title} · {selected.date || `Week ${selected.week}`}
                {selected.version ? ` · v${selected.version}` : ""}
              </h2>
              <p>Original publication record. New predictions do not rewrite this version.</p>
              <dl className="pb-history-facts">
                <div>
                  <dt>Original combined odds</dt>
                  <dd>{fmtOdds(selected.odds)}</dd>
                </div>
                <div>
                  <dt>Published selections</dt>
                  <dd>{selected.legs ?? "—"}</dd>
                </div>
                <div>
                  <dt>Recorded outcome</dt>
                  <dd style={{ fontSize: 13 }}>
                    {selected.result ? (
                      <ResultBadge result={selected.result} />
                    ) : (
                      "Open the full record"
                    )}
                  </dd>
                </div>
              </dl>
              <p>
                Open the original board to inspect every selection, its original quote and the
                latest available settlement status. Missing result data is not a void.
              </p>
              <button className="pb-primary-button" onClick={() => openOriginal(selected)}>
                Open original board <span aria-hidden="true">↗</span>
              </button>
              <p className="pb-subtle">
                Publication prices are historical. They are not current execution quotes.
              </p>
              {!selected.id.startsWith("legacy-") && selected.tier !== "perfect10" && (
                <>
                  <button
                    className="pb-secondary-button"
                    aria-expanded={showLedger}
                    onClick={() => setShowLedger(!showLedger)}
                  >
                    {showLedger ? "Hide selection ledger" : "Show selection ledger"}
                  </button>
                  {showLedger && (
                    <HistorySelectionLedger
                      key={selected.id}
                      id={selected.id}
                      date={selected.date}
                    />
                  )}
                </>
              )}
            </section>
          )}
        </div>
      )}
      <details className="pb-history-accounting">
        <summary>Season accounting · simulated results</summary>
        <p className="pb-subtle">
          Accounting covers PB10 and Mega 25 season records. Returns use verified settlements;
          unresolved tickets are excluded from settled totals.
        </p>
        <div className="pb-history-stat-grid">
          <StatTile
            label="Settled simulated ROI"
            value={
              stats.settledStake > 0
                ? `${((stats.totalReturned / stats.settledStake - 1) * 100).toFixed(1)}% · ${stats.settledTickets} settled`
                : "—"
            }
          />
          <StatTile label="Total attempts" value={fmt(stats.totalAttempts)} />
          <StatTile label="Jackpots" value={fmt(stats.jackpots)} />
          <StatTile label="Near misses" value={fmt(stats.nearMisses)} />
          <StatTile
            label="Avg correct legs"
            value={stats.avgCorrectLegs === null ? "—" : stats.avgCorrectLegs.toFixed(1)}
          />
          <StatTile label="Total staked" value={`GH₵${fmt(stats.totalStaked)}`} />
          <StatTile label="Total returned" value={`GH₵${fmt(stats.totalReturned)}`} />
        </div>
      </details>
      <footer className="pb-workspace-footer">
        <span>Intelligence, not certainty.</span>
        <span>History preserves the original publication.</span>
        <span>Africa/Accra</span>
      </footer>
    </div>
  );
}
